import { type PeniellyShared, sharedAlbumUrl } from "@rhema/client";
import { ArrowUpRightIcon, cn, focusRing, ProductMark } from "@rhema/ui";
import { useEffect, useState } from "react";
import { useLinkStatus, useLinks } from "./useLinks";

/**
 * A Penielly shared-album link in a message. The title, cover and count are only looked up when
 * this person has linked Penielly, so the link stays on the device otherwise.
 */
export function AlbumCard({ token }: { token: string }) {
  const links = useLinks();
  const { status } = useLinkStatus("penielly");
  const linked = Boolean(status?.linked && status.available);
  const [album, setAlbum] = useState<PeniellyShared | null>(null);

  useEffect(() => {
    if (!linked) return;
    let live = true;
    links.penielly
      .shared(token)
      .then((a) => live && setAlbum(a))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [links, token, linked]);

  const title = album?.title || "Penielly album";
  return (
    <a
      href={sharedAlbumUrl(token)}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`Open ${title} in Penielly`}
      className={cn(
        "flex min-w-[220px] items-center gap-3 overflow-hidden rounded-field bg-linen p-2 text-ink no-underline",
        focusRing,
      )}
    >
      {album?.cover_url ? (
        <img src={album.cover_url} alt="" className="h-14 w-14 flex-none rounded-[10px] object-cover" />
      ) : (
        <ProductMark product="penielly" size={56} />
      )}
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="text-[11.5px] text-ink-meta">penielly.com</span>
        <span className="truncate font-serif text-base leading-tight font-semibold">{title}</span>
        <span className="text-[12.5px] text-ink-secondary">
          {album ? (album.count === 1 ? "1 item" : `${album.count} items`) : "Shared album"}
        </span>
      </span>
      <ArrowUpRightIcon size={17} strokeWidth={2.2} className="flex-none text-ink-secondary" />
    </a>
  );
}
